import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Panel } from 'primereact/panel';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import DealerDropdownBySap from '../../../../../components/common/DealerDropdownBySap';
import GenericFileUpload from '../../../../../components/common/GenericFileUpload';
import useAuditForm from './hooks/useAuditForm';
import useAuditDocuments, { ACCEPTED_AUDIT_FILE_TYPES } from './hooks/useAuditDocuments';
import useWarrantyAuditPdf from '../hooks/useWarrantyAuditPdf';
import AuditCriteriaTable from './components/AuditCriteriaTable';
import HistoryMatrix from '../components/HistoryMatrix';

const AuditFormCreateEdit = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { th } = useLanguage();
    const { generate, isGenerating } = useWarrantyAuditPdf();

    const {
        isEdit,
        isLoading,
        isSaving,
        template,
        scores,
        setScore,
        maxTotalScore,
        totalScore,
        currentBand,
        auditDate,
        setAuditDate,
        bosBranchCode,
        setBosBranchCode,
        auditorName,
        savedResult,
        handleSave
    } = useAuditForm(id);

    const readOnly = Boolean(savedResult) || isEdit;
    const auditNo = savedResult?.id || (isEdit ? id : null);
    const displayTotalScore = savedResult ? savedResult.totalScore : totalScore;
    const {
        documents,
        isLoadingDocuments,
        newFiles,
        setNewFiles,
        isProcessingFiles,
        uploadPendingFiles,
        handleDownloadDocument
    } = useAuditDocuments(auditNo);

    const onSave = async () => {
        const result = await handleSave();
        if (result?.id) {
            await uploadPendingFiles(result.id);
            navigate(`/warranty-management/warranty-audit/form/${result.id}`, { replace: true });
        }
    };

    if (isLoading) {
        return (
            <div className="card flex justify-content-center p-5">
                <ProgressSpinner />
            </div>
        );
    }

    return (
        <div className="card">
            <div className="flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                <div className="flex align-items-center gap-2">
                    <Button icon="pi pi-arrow-left" rounded text onClick={() => navigate('/warranty-management/warranty-audit/form')} />
                    <h1 className="m-0">{th('warrantyAudit.form.pageTitle') || 'Garanti Denetim Formu'}</h1>
                </div>
                <div className="flex gap-2">
                    {auditNo && (
                        <Button
                            label={th('warrantyAudit.actions.pdf') || 'PDF'}
                            icon="pi pi-file-pdf"
                            severity="danger"
                            outlined
                            loading={isGenerating}
                            onClick={() => generate(auditNo, { download: false })}
                        />
                    )}
                    {!readOnly && (
                        <Button
                            label={th('warrantyAudit.actions.save') || 'Kaydet'}
                            icon="pi pi-check"
                            loading={isSaving || isProcessingFiles}
                            onClick={onSave}
                        />
                    )}
                </div>
            </div>

            {/* Üst bilgi */}
            <div className="grid formgrid mb-3">
                <div className="col-12 md:col-3">
                    <label className="block mb-2">{th('warrantyAudit.fields.auditNo') || 'Denetim Nr'}</label>
                    <InputText value={auditNo ? String(auditNo) : '-'} disabled className="w-full" />
                </div>
                <div className="col-12 md:col-3">
                    <label className="block mb-2">{th('warrantyAudit.fields.auditDate') || 'Denetim Tarihi'}</label>
                    <Calendar
                        value={auditDate}
                        onChange={(e) => setAuditDate(e.value)}
                        dateFormat="dd/mm/yy"
                        showIcon
                        disabled={readOnly}
                        className="w-full"
                    />
                </div>
                <div className="col-12 md:col-3">
                    <label className="block mb-2">{th('warrantyAudit.fields.dealer') || 'Bayi/Servis'}</label>
                    {readOnly ? (
                        <InputText value={savedResult?.dealerName || (bosBranchCode ?? '')} disabled className="w-full" />
                    ) : (
                        <DealerDropdownBySap
                            value={bosBranchCode}
                            onChange={(dealer) => setBosBranchCode(dealer?.sapDealerId ?? null)}
                            className="w-full"
                        />
                    )}
                </div>
                <div className="col-12 md:col-3">
                    <label className="block mb-2">{th('warrantyAudit.fields.auditor') || 'Denetçi'}</label>
                    <InputText value={auditorName || ''} disabled className="w-full" />
                </div>
            </div>

            {/* Sonuç özeti */}
            <div className="flex align-items-center gap-3 mb-3 flex-wrap">
                <div className="surface-100 border-round p-3">
                    <span className="text-color-secondary">{th('warrantyAudit.fields.totalScore') || 'Toplam Puan'}: </span>
                    <span className="text-2xl font-bold">
                        {displayTotalScore}
                        {maxTotalScore ? ` / ${maxTotalScore}` : ''}
                    </span>
                </div>
                {currentBand && (
                    <span
                        style={{
                            backgroundColor: currentBand.color || '#64748b',
                            color: '#fff',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            fontWeight: 600
                        }}
                    >
                        {currentBand.text}
                    </span>
                )}
            </div>

            {/* Kriter tablosu */}
            <AuditCriteriaTable template={template} scores={scores} setScore={setScore} readOnly={readOnly} th={th} />

            {/* Geçmiş matrisi */}
            {bosBranchCode && (
                <Panel header={th('warrantyAudit.history.title') || 'Denetim Sonucu & Geçmişi'} toggleable className="mt-4">
                    <HistoryMatrix bosBranchCode={bosBranchCode} />
                </Panel>
            )}
            {/* Dosya ekleme */}
            <Panel header={th('warrantyAudit.documents.title') || 'Dosyalar'} toggleable className="mt-4">
                {!readOnly && (
                    <GenericFileUpload
                        newFiles={newFiles}
                        onFilesChange={setNewFiles}
                        existingDocuments={documents}
                        onDownloadDocument={handleDownloadDocument}
                        isLoading={isLoadingDocuments}
                        maxFileSize={20971520}
                        disabled={isProcessingFiles}
                        multiple
                        acceptedFileTypes={ACCEPTED_AUDIT_FILE_TYPES}
                    />
                )}

                {readOnly && isLoadingDocuments && (
                    <div className="flex justify-content-center align-items-center py-3">
                        <ProgressSpinner style={{ width: '30px', height: '30px' }} />
                    </div>
                )}

                {readOnly && !isLoadingDocuments && documents.length === 0 && (
                    <span className="text-color-secondary">{th('warrantyAudit.documents.empty') || 'Dosya yok'}</span>
                )}

                {readOnly && !isLoadingDocuments && documents.length > 0 && (
                    <div className="flex flex-column gap-2">
                        {documents.map((doc) => (
                            <div key={doc.id || doc.fileKey || doc.fileName} className="inline-flex align-items-center gap-2">
                                <span className="text-sm font-medium white-space-nowrap overflow-hidden text-overflow-ellipsis" title={doc.fileName}>
                                    {doc.fileName}
                                </span>
                                <Button
                                    type="button"
                                    icon="pi pi-download"
                                    text
                                    rounded
                                    onClick={() => handleDownloadDocument(doc)}
                                    tooltip={th('fileUpload.download') || 'İndir'}
                                    tooltipOptions={{ position: 'top' }}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </Panel>
        </div>
    );
};

export default AuditFormCreateEdit;
